import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import LinearProgress from "@material-ui/core/LinearProgress";
import { useAPI } from "../context/ApiContext";
import { SnackbarContext } from "../context/SnackbarContext";
import { useTranslation } from "react-i18next";

const MAX_LEN = 2000;

// Createpurepost.jsx 内（リンクプレビュー部分のみ置換）

const YouTubeEmbed = ({ embedUrl, title }) => {
  if (!embedUrl) return null;
  return (
    <div style={{ position: "relative", width: "100%", paddingTop: "56.25%", borderRadius: 12, overflow: "hidden" }}>
      <iframe
        src={embedUrl}
        title={title || "YouTube video"}
        style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: 0 }}
        loading="lazy"
        referrerPolicy="strict-origin-when-cross-origin"
        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    </div>
  );
};

const TweetEmbed = ({ embedUrl }) => {
  if (!embedUrl) return null;
  // 公式の Tweet embed ページは高さ自動調整が効くため iframe でOK
  return (
    <div style={{ position: "relative", width: "100%", minHeight: 220, borderRadius: 12, overflow: "hidden" }}>
      <iframe
        src={embedUrl}
        title="Tweet"
        style={{ width: "100%", height: 420, border: 0 }}
        loading="lazy"
        allowTransparency
      />
    </div>
  );
};

const InstagramEmbed = ({ embedUrl }) => {
  if (!embedUrl) return null;
  return (
    <div style={{ position: "relative", width: "100%", borderRadius: 12, overflow: "hidden" }}>
      <iframe
        src={embedUrl}
        title="Instagram"
        style={{ width: "100%", height: 480, border: 0 }}
        loading="lazy"
        referrerPolicy="strict-origin-when-cross-origin"
      />
    </div>
  );
};

const TikTokEmbed = ({ embedUrl }) => {
  if (!embedUrl) return null;
  return (
    <div style={{ position: "relative", width: "100%", paddingTop: "56.25%", borderRadius: 12, overflow: "hidden", background: "#000" }}>
      <iframe
        src={embedUrl}
        title="TikTok"
        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; fullscreen"
        allowFullScreen
        loading="lazy"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
      />
    </div>
  );
};


const LinkPreviewCard = ({ preview, url, loading, error }) => {
  const { imageProxyUrl } = useAPI(); // ★ 追加：ApiContext から取得
  const host = React.useMemo(() => {
    try { return new URL(url).hostname; } catch { return ""; }
  }, [url]);
  const favicon = host ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=64` : "";

  if (!url) return null;

  // ローディング／エラー表示
  if (loading) {
    return (
      <div className="card w-100 shadow-xss rounded-xxl border-light-md p-3 mb-2">
        <div className="d-flex align-items-center">
          <CircularProgress size={18} className="me-2" />
          <span className="font-xssss text-grey-600">プレビューを取得中…</span>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="card w-100 shadow-xss rounded-xxl border-light-md p-3 mb-2">
        <div className="d-flex align-items-center">
          <img src={favicon} alt="" className="me-2" style={{ width: 20, height: 20, borderRadius: 4 }} />
          <div className="flex-fill">
            <div className="font-xssss text-danger">プレビューを取得できませんでした</div>
            <div className="font-xssss text-grey-600 text-truncate">{url}</div>
          </div>
        </div>
      </div>
    );
  }

  // 画像URLの http をプロキシ化（httpsページでのMixed Content回避）
  const resolveImage = (u) => {
    if (!u) return "";
    try {
      const a = new URL(u);
      return a.protocol === "http:" ? imageProxyUrl(u) : u;
    } catch {
      return u;
    }
  };

  // 種別分岐
  if (preview?.kind === "image") {
    const src = resolveImage(preview.image || url);
    return (
      <div className="card w-100 shadow-xss rounded-xxl border-light-md p-3 mb-2">
        {src ? (
          <img
            src={src}
            alt=""
            className="rounded-xxl"
            style={{ width: "100%", maxHeight: 420, objectFit: "contain", background: "#f6f6f6" }}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : null}
        <div className="font-xssss text-grey-500 mt-2 text-truncate">{host}</div>
      </div>
    );
  }

  if (preview?.kind === "youtube") {
    return (
      <div className="card w-100 shadow-xss rounded-xxl border-light-md p-3 mb-2">
        <YouTubeEmbed embedUrl={preview.embedUrl} title={preview.title || "YouTube"} />
        <div className="mt-2">
          <div className="font-xsss fw-700 text-dark text-truncate">{preview.title || host}</div>
          <div className="font-xssss text-grey-500 mt-1 text-truncate">{host}</div>
        </div>
      </div>
    );
  }

  if (preview?.kind === "tweet") {
    return (
      <div className="card w-100 shadow-xss rounded-xxl border-light-md p-3 mb-2">
        <TweetEmbed embedUrl={preview.embedUrl} />
        <div className="mt-2">
          <div className="font-xssss text-grey-500 text-truncate">{host}</div>
        </div>
      </div>
    );
  }

  if (preview?.kind === "instagram") {
    const thumb = resolveImage(preview.image);
    return (
      <div className="card w-100 shadow-xss rounded-xxl border-light-md p-3 mb-2">
        <InstagramEmbed embedUrl={preview.embedUrl} />
        {thumb && (
          <img
            src={thumb}
            alt=""
            className="rounded-xxl mt-2"
            style={{ width: "100%", maxHeight: 320, objectFit: "cover" }}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        )}
        <div className="mt-2">
          <div className="font-xssss text-grey-500 text-truncate">{host}</div>
        </div>
      </div>
    );
  }

  if (preview?.kind === "tiktok") {
    const thumb = resolveImage(preview.image);
    return (
      <div className="card w-100 shadow-xss rounded-xxl border-light-md p-3 mb-2">
        <TikTokEmbed embedUrl={preview.embedUrl} />
        {thumb && (
          <img
            src={thumb}
            alt=""
            className="rounded-xxl mt-2"
            style={{ width: "100%", maxHeight: 320, objectFit: "cover" }}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        )}
        <div className="mt-2">
          <div className="font-xssss text-grey-500 text-truncate">{host}</div>
        </div>
      </div>
    );
  }




  // 通常リンク（OGP）
  const og = {
    title: preview?.title || host,
    description: preview?.description || "",
    image: resolveImage(preview?.image || ""),
  };

  return (
    <div className="card w-100 shadow-xss rounded-xxl border-light-md p-3 mb-2">
      <div className="d-flex">
        {og.image ? (
          <img
            src={og.image}
            alt=""
            className="me-3 rounded-xxl"
            style={{ width: 96, height: 96, objectFit: "cover" }}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <img src={favicon} alt="" className="me-3" style={{ width: 32, height: 32, borderRadius: 6 }} />
        )}
        <div className="flex-fill" style={{ minWidth: 0 }}>
          <div className="font-xsss fw-700 text-dark text-truncate">{og.title}</div>
          {og.description && (
            <div
              className="font-xssss text-grey-600"
              style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
            >
              {og.description}
            </div>
          )}
          <div className="font-xssss text-grey-500 mt-1 text-truncate">{host}</div>
        </div>
      </div>
    </div>
  );
};


/**
 * nicelinks 投稿モーダル（文字数ゲージ + URLプレビュー）
 */
const Createpurepost = ({ isVisible, handleClose, onPosted, initialUrl = "" }) => {
  const { t } = useTranslation();
  const { newSnack } = useContext(SnackbarContext);
  const { isAuthenticated, createPost, unfurlUrl } = useAPI();

  const [postText, setPostText] = useState("");
  const [postUrl, setPostUrl] = useState("");
  const [sensitiveFlag, setSensitiveFlag] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // プレビュー状態
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");

  useEffect(() => {
    if (!isVisible) {
      setPostText("");
      setPostUrl("");
      setSensitiveFlag(false);
      setSubmitting(false);
      setError("");
      setPreview(null);
      setPreviewError("");
      setPreviewLoading(false);
    }
    // 開いたら初期URLをセット
    if (initialUrl) setPostUrl(initialUrl);
      }, [isVisible, initialUrl]);

  const validUrlOrEmpty = useCallback((v) => {
    if (!v) return true;
    try { new URL(v); return true; } catch { return false; }
  }, []);

  // URL が変わったら 500ms デバウンスしてアンファール
  useEffect(() => {
    setPreview(null);
    setPreviewError("");
    if (!postUrl || !validUrlOrEmpty(postUrl)) return;
    const id = setTimeout(async () => {
      try {
        setPreviewLoading(true);
        const data = await unfurlUrl(postUrl);
        if (data?.ok) {
          setPreview({
            title: data.title || "",
            description: data.description || "",
            image: data.image || "",
            provider: data.provider || "",
          });
          setPreviewError("");
        } else {
          setPreview(null);
          setPreviewError(data?.error || "unfurl failed");
        }
      } catch (e) {
        setPreview(null);
        setPreviewError("unfurl error");
      } finally {
        setPreviewLoading(false);
      }
    }, 500);
    return () => clearTimeout(id);
  }, [postUrl, unfurlUrl, validUrlOrEmpty]);

  const percent = Math.min(100, Math.round((postText.length / MAX_LEN) * 100));
  const gaugeColor = percent < 80 ? "primary" : percent < 95 ? "secondary" : "secondary";

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    if (!isAuthenticated) {
      newSnack("error", t("LoginRequired") || "投稿するにはログインしてください");
      return;
    }
    const text = postText.trim();
    if (!text) {
      setError(t("PostTextRequired") || "本文は必須です");
      return;
    }
    if (!validUrlOrEmpty(postUrl)) {
      setError(t("InvalidUrl") || "URLの形式が正しくありません");
      return;
    }
    setSubmitting(true);
    try {
      const created = await createPost({
        postText: text,
        postUrl: postUrl.trim() || null,
        sensitiveFlag,
      });
      newSnack("success", t("Posted") || "投稿しました");
      onPosted?.(created);
      handleClose();
    } catch (err) {
      setError(err?.data?.detail || err.message || (t("PostFailed") || "投稿に失敗しました"));
      newSnack("error", t("PostFailed") || "投稿に失敗しました");
    } finally {
      setSubmitting(false);
    }
  };

  const onKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") onSubmit(e);
  };

  return (
    <Dialog
      className="animated p-0"
      open={isVisible}
      onClose={handleClose}
      aria-labelledby="post-dialog"
      fullWidth
      maxWidth="sm"
    >
      <DialogContent className="card w-000 shadow-xss rounded-xxl border-0 ps-0 pt-0 pe-0 pb-3 mt-1 mb-1">
        <div className="position-absolute right-0 top-0 pointer me-3" onClick={handleClose}>
          <i className="ti-close text-grey-400 fw-700 font-xsss"></i>
        </div>

        <form onSubmit={onSubmit}>
          {/* 本文 */}
          <div className="card-body ps-2 pe-2 pt-4 pb-1 position-relative">
            <textarea
              value={postText}
              onChange={(e) => {
                const v = e.target.value.slice(0, MAX_LEN);
                setPostText(v);
                setError("");
              }}
              onKeyDown={onKeyDown}
              className="h100 bor-0 w-100 rounded-xxl p-2 ps-3 font-xss fw-500 theme-dark-text border-light-md theme-dark-bg mb-2"
              rows={6}
              placeholder={t("Createpost1")}
            />
            {/* 文字数ゲージ */}
            <div className="d-flex align-items-center">
              <div className="flex-grow-1 me-2">
                <LinearProgress
                  variant="determinate"
                  value={percent}
                  color={gaugeColor}
                  style={{ height: 6, borderRadius: 6 }}
                />
              </div>
              <small className={`fw-600 ${percent >= 95 ? "text-danger" : "text-grey-600"}`}>
                {postText.length}/{MAX_LEN}
              </small>
            </div>
            <div className="d-flex justify-content-end mt-1">
              <label className="d-flex align-items-center font-xssss text-grey-700 mb-0">
                <input
                  type="checkbox"
                  className="me-2"
                  checked={sensitiveFlag}
                  onChange={(e) => setSensitiveFlag(e.target.checked)}
                />
                {t("Createpost2")}
              </label>
            </div>
          </div>

          {/* URL（任意） + プレビュー */}
          <div className="card-body ps-2 pe-2 pt-2 pb-1">
            <input
              type="url"
              value={postUrl}
              onChange={(e) => { setPostUrl(e.target.value); setError(""); }}
              className="style2-input form-control text-grey-900 font-xsss fw-600 mb-2"
              placeholder={t("Createpost6")}
            />
            {!validUrlOrEmpty(postUrl) && (
              <small className="text-danger">{t("Createpost3")}</small>
            )}
            <LinkPreviewCard
              url={postUrl}
              preview={preview}
              loading={previewLoading}
              error={previewError}
            />
          </div>

          {/* エラー */}
          {error && (
            <div className="card-body pt-1 pb-0">
              <p className="text-danger font-xssss mb-0">{error}</p>
            </div>
          )}

          {/* アクション */}
          <div className="card-body d-flex justify-content-end gap-2 pt-2">
            <Button
              onClick={handleClose}
              className="bg-dark text-white font-xsss fw-600 pt-1 pb-1 ps-3 pe-3 rounded-3"
              type="button"
              disabled={submitting}
            >
              {t("Createpost4")}
            </Button>
            <Button
              className="bg-current text-white font-xsss fw-600 pt-1 pb-1 ps-3 pe-3 rounded-3"
              variant="contained"
              type="submit"
              disabled={submitting || !postText.trim() || !validUrlOrEmpty(postUrl)}
            >
              {submitting ? <CircularProgress size={18} color="inherit" /> : (t("Createpost5") || "投稿する")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default Createpurepost;
